import React from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';

import { containerStyles } from '../../styles/layout';
import { headers, body } from '../../styles/type';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';

import Image from '../Image';
import Button from '../buttons/Button';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  padding: ${rem(48)} 0;
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Headline = styled.h2`
  ${headers.h3}
  margin-bottom: ${rem(40)};
`;

const ResourceItems = styled.div`

`;

const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(40)};
  padding: ${rem(24)} 0;

    & + & {
      border-top: 1px solid ${colors.gray['200']};
    }
`;

const IconWrapper = styled.div`
  flex: 0 1 ${rem(120)};
  aspect-ratio: 1 / 1;
`;

const Icon = styled(Image)`
  border-radius: ${rem(8)};
  object-fit: cover;
  height: 100%;
  width: 100%;
`;

const ResourceItemContent = styled.div`
  max-width: ${rem(900)};
  flex: 1 1 ${rem(450)};
  margin-right: auto;
`;

const ResourceItemHeadline = styled.h3`
  ${headers.h6}
  margin-bottom: ${rem(12)};
`;

const ResourceItemDescription = styled.p`
  ${body.regular}
`;

const ResourceRows = ({ headline, items }) => (
  <Container>
    <ContentWrapper>
      {headline && (
        <Headline>
          <TextRenderer text={headline} />
        </Headline>
      )}
      <ResourceItems>
        {items && items.map(({ icon, title, description, ctaText, ctaLink }) => (
          <ResourceItem>
            {icon && (
              <IconWrapper>
                <Icon image={icon} objectFit="cover" />
              </IconWrapper>
            )}
            <ResourceItemContent>
              {title && (
                <ResourceItemHeadline>
                  <TextRenderer text={title} />
                </ResourceItemHeadline>
              )}
              {description && (
                <ResourceItemDescription>
                  <TextRenderer text={description} />
                </ResourceItemDescription>
              )}
            </ResourceItemContent>
            {ctaText && ctaLink && (
              <Button to={ctaLink}>
                <TextRenderer text={ctaText} />
              </Button>
            )}
          </ResourceItem>
        ))}
      </ResourceItems>
    </ContentWrapper>
  </Container>
);

ResourceRows.propTypes = {
  headline: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      ctaText: PropTypes.string,
      ctaLink: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      icon: PropTypes.object,
    })
  ),

};

export const query = graphql`
  fragment ResourceRowsFragment on ContentfulResourceRows {
    id
    headline
    __typename
    items {
      title
      description
      ctaLink
      ctaText
      icon {
        gatsbyImageData(width: 120, height: 120, quality: 75)
        ...ImageAssetFragment
      }
    }
  }
`;

export default ResourceRows;
